// # Task Statistics Component for Step 1
import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './task_stats2.css';

// # Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const TaskStats = ({ rows }) => {
  // # State for collapsible panel
  const [isExpanded, setIsExpanded] = useState(false);
  
  // # State for statistics
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    completionPercentage: 0,
    totalDurationHours: 0,
    completedDurationHours: 0,
    durationCompletionPercentage: 0,
    tasksByPlan: {},
    tasksBySource: {},
    durationByPlan: {}
  });

  // # Calculate statistics when rows change
  useEffect(() => {
    if (!rows || rows.length === 0) {
      return;
    }

    // # Initialize counters
    let totalTasks = rows.length;
    let completedTasks = 0;
    let totalDurationMinutes = 0;
    let completedDurationMinutes = 0;
    
    // # Initialize objects to store counts by category
    const tasksByPlan = {};
    const tasksBySource = {};
    const durationByPlan = {};
    
    // # Process each task
    rows.forEach(task => {
      // # Count completed tasks
      if (task.done) {
        completedTasks++;
        // # Add duration of completed tasks (convert to minutes if it's a string)
        const duration = typeof task.duration === 'string' ? parseInt(task.duration, 10) || 0 : task.duration || 0;
        completedDurationMinutes += duration;
      }
      
      // # Add total duration (convert to minutes if it's a string)
      const duration = typeof task.duration === 'string' ? parseInt(task.duration, 10) || 0 : task.duration || 0;
      totalDurationMinutes += duration;
      
      // # Count tasks by plan
      const plan = task.plan || 'Unassigned';
      tasksByPlan[plan] = (tasksByPlan[plan] || 0) + 1;
      
      // # Count tasks by source
      const source = task.source || 'Unassigned';
      tasksBySource[source] = (tasksBySource[source] || 0) + 1;
      
      // # Sum duration by plan
      durationByPlan[plan] = (durationByPlan[plan] || 0) + duration;
    });
    
    // # Calculate percentages
    const completionPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    const durationCompletionPercentage = totalDurationMinutes > 0 ? (completedDurationMinutes / totalDurationMinutes) * 100 : 0;
    
    // # Convert minutes to hours
    const totalDurationHours = totalDurationMinutes / 60;
    const completedDurationHours = completedDurationMinutes / 60;
    
    // # Convert duration by plan from minutes to hours
    const durationByPlanHours = {};
    Object.keys(durationByPlan).forEach(plan => {
      durationByPlanHours[plan] = durationByPlan[plan] / 60;
    });
    
    // # Update stats state
    setStats({
      totalTasks,
      completedTasks,
      completionPercentage,
      totalDurationHours,
      completedDurationHours,
      durationCompletionPercentage,
      tasksByPlan,
      tasksBySource,
      durationByPlan: durationByPlanHours
    });
  }, [rows]);

  // # Generate chart data for tasks by plan
  const tasksByPlanData = {
    labels: Object.keys(stats.tasksByPlan),
    datasets: [
      {
        label: 'Tasks by Plan',
        data: Object.values(stats.tasksByPlan),
        backgroundColor: [
          'rgba(46, 125, 50, 0.7)',  // Plan A - green
          'rgba(21, 101, 192, 0.7)',  // Plan B - blue
          'rgba(106, 27, 154, 0.7)',  // Plan C - purple
          'rgba(158, 158, 158, 0.7)'  // Unassigned - gray
        ],
        borderColor: [
          'rgba(46, 125, 50, 1)',
          'rgba(21, 101, 192, 1)',
          'rgba(106, 27, 154, 1)',
          'rgba(158, 158, 158, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // # Generate chart data for tasks by source
  const tasksBySourceData = {
    labels: Object.keys(stats.tasksBySource),
    datasets: [
      {
        label: 'Tasks by Source',
        data: Object.values(stats.tasksBySource),
        backgroundColor: [
          'rgba(168, 216, 234, 0.7)',  // BnB
          'rgba(199, 206, 234, 0.7)',  // Pathoma
          'rgba(226, 240, 203, 0.7)',  // Husseiny
          'rgba(240, 230, 239, 0.7)',  // Ta7
          'rgba(255, 218, 193, 0.7)',  // Physeo
          'rgba(255, 207, 210, 0.7)',  // Sketchy
          'rgba(212, 240, 240, 0.7)',  // Picorize
          'rgba(255, 216, 184, 0.7)',  // Mehlman
          'rgba(181, 234, 215, 0.7)',  // Dirty
          'rgba(255, 183, 178, 0.7)',  // BootCamp
          'rgba(200, 200, 200, 0.7)'   // Unassigned
        ],
        borderWidth: 1,
      },
    ],
  };

  // # Generate chart data for duration by plan
  const durationByPlanData = {
    labels: Object.keys(stats.durationByPlan),
    datasets: [
      {
        label: 'Duration (hours)',
        data: Object.values(stats.durationByPlan),
        backgroundColor: [
          'rgba(46, 125, 50, 0.7)',  // Plan A - green
          'rgba(21, 101, 192, 0.7)',  // Plan B - blue
          'rgba(106, 27, 154, 0.7)',  // Plan C - purple
          'rgba(158, 158, 158, 0.7)'  // Unassigned - gray
        ],
        borderColor: [
          'rgba(46, 125, 50, 1)',
          'rgba(21, 101, 192, 1)',
          'rgba(106, 27, 154, 1)',
          'rgba(158, 158, 158, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // # Generate chart data for completion status
  const completionStatusData = {
    labels: ['Completed', 'Remaining'],
    datasets: [
      {
        label: 'Tasks',
        data: [stats.completedTasks, stats.totalTasks - stats.completedTasks],
        backgroundColor: [
          'rgba(46, 204, 113, 0.7)',  // Completed - green
          'rgba(231, 76, 60, 0.7)'    // Remaining - red
        ],
        borderColor: [
          'rgba(46, 204, 113, 1)',
          'rgba(231, 76, 60, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // # Generate chart data for duration completion
  const durationCompletionData = {
    labels: ['Completed', 'Remaining'],
    datasets: [
      {
        label: 'Duration (hours)',
        data: [stats.completedDurationHours, stats.totalDurationHours - stats.completedDurationHours],
        backgroundColor: [
          'rgba(46, 204, 113, 0.7)',  // Completed - green
          'rgba(231, 76, 60, 0.7)'    // Remaining - red
        ],
        borderColor: [
          'rgba(46, 204, 113, 1)',
          'rgba(231, 76, 60, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // # Chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw.toFixed(1);
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  // # Format number with 1 decimal place
  const formatNumber = (num) => {
    return parseFloat(num.toFixed(1));
  };

  return (
    <div className="task_stats2_container">
      <div 
        className="task_stats2_header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Task Statistics Dashboard</h3>
        <button className="task_stats2_toggle">
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>
      
      {isExpanded && (
        <div className="task_stats2_content">
          {/* Summary Statistics */}
          <div className="task_stats2_summary">
            <div className="task_stats2_card">
              <h4>Total Tasks</h4>
              <div className="task_stats2_value">{stats.totalTasks}</div>
            </div>
            
            <div className="task_stats2_card">
              <h4>Completed Tasks</h4>
              <div className="task_stats2_value">{stats.completedTasks}</div>
              <div className="task_stats2_percentage">
                {formatNumber(stats.completionPercentage)}%
              </div>
            </div>
            
            <div className="task_stats2_card">
              <h4>Total Duration</h4>
              <div className="task_stats2_value">{formatNumber(stats.totalDurationHours)} hours</div>
            </div>
            
            <div className="task_stats2_card">
              <h4>Completed Duration</h4>
              <div className="task_stats2_value">{formatNumber(stats.completedDurationHours)} hours</div>
              <div className="task_stats2_percentage">
                {formatNumber(stats.durationCompletionPercentage)}%
              </div>
            </div>
          </div>
          
          {/* Charts */}
          <div className="task_stats2_charts">
            <div className="task_stats2_chart_row">
              <div className="task_stats2_chart_container">
                <h4>Tasks by Plan</h4>
                <div className="task_stats2_chart">
                  <Pie data={tasksByPlanData} options={pieOptions} />
                </div>
              </div>
              
              <div className="task_stats2_chart_container">
                <h4>Tasks by Source</h4>
                <div className="task_stats2_chart">
                  <Pie data={tasksBySourceData} options={pieOptions} />
                </div>
              </div>
            </div>
            
            <div className="task_stats2_chart_row">
              <div className="task_stats2_chart_container">
                <h4>Task Completion Status</h4>
                <div className="task_stats2_chart">
                  <Pie data={completionStatusData} options={pieOptions} />
                </div>
              </div>
              
              <div className="task_stats2_chart_container">
                <h4>Duration Completion Status</h4>
                <div className="task_stats2_chart">
                  <Pie data={durationCompletionData} options={pieOptions} />
                </div>
              </div>
            </div>
            
            <div className="task_stats2_chart_row">
              <div className="task_stats2_chart_container full_width">
                <h4>Duration by Plan (hours)</h4>
                <div className="task_stats2_chart bar_chart">
                  <Bar data={durationByPlanData} options={barOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskStats;
